import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import ReactDOM from "react-dom";
import { getRoles, deleteRole } from "../../../redux/Slices/RolesSlice";
import { FaPlus, FaEdit, FaTrash, FaShieldAlt, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../shared/hooks/TitleContext";
import i18n from "../../../i18n/i18n";
import { getTextDirection, sortListById } from "../../../shared/utils.js/utils";
import SortOrderSelect from "../../../shared/components/SortOrderSelect";
import SearchInput from "../../../shared/components/SearchInput";

const PER_PAGE = 10;

const Roles = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";

  const [allRoles, setAllRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState([]);
  const [selectedRoleName, setSelectedRoleName] = useState("");

  useEffect(() => {
    setTitle("sidenav.roles");
  }, [setTitle]);

  const getRoleDisplayName = role => {
    if (!role) return "";
    const keyFromApi = role.name || role.translation;
    const baseName = isRTL
      ? role.translation || keyFromApi
      : keyFromApi || role.translation;
    if (!keyFromApi) return baseName || "";
    const translationKey = keyFromApi.startsWith("roles.")
      ? keyFromApi
      : `roles.${keyFromApi}`;
    return t(translationKey, { defaultValue: baseName || keyFromApi });
  };

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(getRoles({ page: 1, per_page: 1000 }));
      if (getRoles.fulfilled.match(result)) {
        const response = result.payload;
        const data = response?.data || response;
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        setAllRoles(list);
      } else {
        toast.error(result.payload || "Failed to fetch roles", { rtl: isRTL });
      }
    } catch (err) {
      toast.error(err.message || "Failed to fetch roles", { rtl: isRTL });
    } finally {
      setIsLoading(false);
    }
  };

  // Single fetch on mount
  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset page when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder]);

  // Client-side filtering (name + translation) + sorting — instant, no debounce needed
  const filteredRoles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const sorted = sortListById(allRoles, sortOrder);
    if (!term) return sorted;
    return sorted.filter(
      role =>
        (role.name || "").toLowerCase().includes(term) ||
        (role.translation || "").toLowerCase().includes(term),
    );
  }, [allRoles, searchTerm, sortOrder]);

  const totalPages = Math.ceil(filteredRoles.length / PER_PAGE) || 1;
  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );

  const handleAdd = () => navigate("/roles/add");
  const handleEdit = id => navigate(`/roles/edit/${id}`);

  const handleDelete = role => {
    setRoleToDelete(role);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      const result = await dispatch(deleteRole(roleToDelete.id));
      if (deleteRole.fulfilled.match(result)) {
        toast.success(
          t("roles.delete.success", { defaultValue: "تم حذف الدور بنجاح" }),
          { rtl: isRTL },
        );
        setShowDeleteModal(false);
        setRoleToDelete(null);
        fetchRoles();
      } else {
        toast.error(
          result.payload ||
            t("roles.delete.error", { defaultValue: "فشل حذف الدور" }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("roles.delete.error", { defaultValue: "فشل حذف الدور" }),
        { rtl: isRTL },
      );
    }
  };

  const handleAssignPermissions = id => navigate(`/roles/${id}/permissions`);

  if (isLoading && allRoles.length === 0) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {t("common.loading", { defaultValue: "جاري التحميل..." })}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
          {t("sidenav.roles", { defaultValue: "الأدوار" })}
        </h1>
        <button
          onClick={handleAdd}
          style={{
            backgroundColor: "#AC2000",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaPlus /> {t("roles.add", { defaultValue: "إضافة دور" })}
        </button>
      </div>

      {/* Search + Sort */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-end",
          flexWrap: "wrap",
          marginBottom: "20px",
        }}
      >
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t("roles.searchPlaceholder", {
            defaultValue: "بحث بالأدوار...",
          })}
          isLoading={isLoading}
          isRTL={isRTL}
        />
        <SortOrderSelect value={sortOrder} onChange={setSortOrder} />
      </div>

      {/* Table */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #333",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "600px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {t("roles.table.number", { defaultValue: "#" })}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {t("roles.table.name", { defaultValue: "اسم الدور" })}
                </th>
                <th
                  style={{
                    padding: "15px",
                    textAlign: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {t("roles.table.actions", { defaultValue: "الإجراءات" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRoles.length > 0 ? (
                paginatedRoles.map((role, index) => (
                  <tr key={role.id} style={{ borderBottom: "1px solid #333" }}>
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        textAlign: "center",
                      }}
                    >
                      {(currentPage - 1) * PER_PAGE + index + 1}
                    </td>
                    <td
                      style={{
                        padding: "15px",
                        color: "white",
                        textAlign: "center",
                      }}
                    >
                      {getRoleDisplayName(role)}
                    </td>
                    <td style={{ padding: "15px", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                          flexWrap: "nowrap",
                          alignItems: "center",
                        }}
                      >
                        <button
                          onClick={() => {
                            setSelectedRolePermissions(role.permissions || []);
                            setSelectedRoleName(getRoleDisplayName(role));
                            setShowPermissionsModal(true);
                          }}
                          style={{
                            backgroundColor: "#2f3644",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("roles.viewPermissions.button", {
                            defaultValue: "عرض التفاصيل",
                          })}
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleAssignPermissions(role.id)}
                          style={{
                            backgroundColor: "#17a2b8",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("roles.assignPermissions.title", {
                            defaultValue: "تعيين الصلاحيات",
                          })}
                        >
                          <FaShieldAlt />
                        </button>
                        <button
                          onClick={() => handleEdit(role.id)}
                          style={{
                            backgroundColor: "#B3B3B3",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("common.edit", { defaultValue: "تعديل" })}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(role)}
                          style={{
                            backgroundColor: "#dc3545",
                            color: "white",
                            border: "none",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "32px",
                            height: "32px",
                          }}
                          title={t("common.delete", {
                            defaultValue: "حذف",
                          })}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    style={{
                      textAlign: "center",
                      color: "white",
                      padding: "20px",
                    }}
                  >
                    {searchTerm.trim()
                      ? t("roles.noSearchResults", {
                          defaultValue: "لا توجد نتائج للبحث",
                        })
                      : t("roles.noData", { defaultValue: "لا توجد أدوار" })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — client-side over filteredRoles */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "20px",
              paddingBottom: "20px",
            }}
          >
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: "8px 12px",
                backgroundColor: currentPage === 1 ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              {t("common.previous", { defaultValue: "السابق" })}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                page =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2),
              )
              .map((page, idx, arr) => {
                if (idx > 0 && page - arr[idx - 1] > 1) {
                  return (
                    <React.Fragment key={`ellipsis-${page}`}>
                      <span style={{ padding: "8px", color: "white" }}>
                        ...
                      </span>
                      <button
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            page === currentPage ? "#AC2000" : "#1a1f2e",
                          color: "white",
                          border: "1px solid #333",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: "8px 12px",
                      backgroundColor:
                        page === currentPage ? "#AC2000" : "#1a1f2e",
                      color: "white",
                      border: "1px solid #333",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 12px",
                backgroundColor:
                  currentPage === totalPages ? "#333" : "#AC2000",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              }}
            >
              {t("common.next", { defaultValue: "التالي" })}
            </button>
          </div>
        )}
      </div>

      {/* Permissions Details Modal */}
      {showPermissionsModal &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 99999,
            }}
            onClick={() => {
              setShowPermissionsModal(false);
              setSelectedRolePermissions([]);
              setSelectedRoleName("");
            }}
          >
            <div
              style={{
                backgroundColor: "#1a1f2e",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "600px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
                overflowX: "hidden",
                ...getTextDirection(),
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  color: "white",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  fontSize: "18px",
                  ...getTextDirection(),
                }}
              >
                {t("roles.viewPermissions.title", {
                  defaultValue: "صلاحيات الدور",
                })}
                : {selectedRoleName}
              </h3>
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    marginBottom: "10px",
                    color: "white",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    ...getTextDirection(),
                  }}
                >
                  {t("roles.viewPermissions.count", {
                    defaultValue: "عدد الصلاحيات",
                  })}
                  : {selectedRolePermissions.length}
                </div>
                <div
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    padding: "10px",
                    backgroundColor: "#14263b",
                    ...getTextDirection(),
                  }}
                >
                  {selectedRolePermissions.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {selectedRolePermissions.map((permission, index) => (
                        <div
                          key={permission.id || index}
                          style={{
                            padding: "10px",
                            backgroundColor: "#1b3350",
                            borderRadius: "6px",
                            border: "1px solid #333",
                            color: "white",
                            fontSize: "14px",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            ...getTextDirection(),
                          }}
                        >
                          {permission.translation ||
                            permission.name ||
                            `Permission ${permission.id}`}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#888",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        ...getTextDirection(),
                      }}
                    >
                      {t("roles.viewPermissions.noPermissions", {
                        defaultValue: "لا توجد صلاحيات",
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setSelectedRolePermissions([]);
                    setSelectedRoleName("");
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                  }}
                >
                  {t("btns.close", { defaultValue: "إغلاق" })}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "12px",
              }}
            >
              <div
                className="modal-header"
                style={{ border: "none", borderBottom: "1px solid #333" }}
              >
                <h5 className="modal-title text-white" style={{ textAlign }}>
                  {t("roles.delete.title", {
                    defaultValue: "تأكيد الحذف",
                  })}
                </h5>
              </div>
              <div className="modal-body text-white" style={{ textAlign }}>
                <p>
                  {t("roles.delete.message", {
                    defaultValue: "هل أنت متأكد من حذف الدور",
                  })}{" "}
                  "{roleToDelete?.translation || roleToDelete?.name}"؟
                </p>
              </div>
              <div className="modal-footer" style={{ border: "none" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: "#6c757d",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                >
                  {t("btns.cancel", { defaultValue: "إلغاء" })}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: "#dc3545",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                >
                  {t("btns.delete", { defaultValue: "حذف" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
